import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, Printer, ListOrdered } from "lucide-react";
import { api, formatMoney } from "@/lib/api";

const valid = (v) =>
  v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v)) && Number(v) > 0;

function rateFor(product, unit) {
  const r = (product.unit_rates || []).find((u) => u && u.unit === unit && valid(u.rate));
  return r ? Number(r.rate) : null;
}

function displayMrp(product) {
  if (valid(product.mrp)) return Number(product.mrp);
  const m = (product.unit_mrps || []).find((x) => x && valid(x.mrp));
  return m ? Number(m.mrp) : null;
}

const dash = <span className="text-slate-300">—</span>;

export default function PriceListSection() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("all");

  useEffect(() => {
    setLoading(true);
    api
      .get("/products")
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, []);

  const brandOptions = useMemo(() => {
    const set = new Set(products.map((p) => p.brand).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchBrand = brand === "all" || p.brand === brand;
      const matchQuery =
        !q || p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q);
      return matchBrand && matchQuery;
    });
  }, [products, query, brand]);

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const b = p.brand || "Others";
      (map[b] = map[b] || []).push(p);
    });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b))
      .map((b) => ({
        brand: b,
        items: map[b].slice().sort((a, c) => a.name.localeCompare(c.name)),
      }));
  }, [filtered]);

  const today = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div data-testid="pricelist-section">
      {/* Controls (hidden when printing) */}
      <div className="no-print mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Price List</h2>
          <p className="text-sm text-slate-500">{filtered.length} products · rates update automatically</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="pricelist-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <select
            data-testid="pricelist-brand-filter"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:w-48"
          >
            <option value="all">All Brands</option>
            {brandOptions.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <button
            data-testid="pricelist-print-button"
            onClick={() => window.print()}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Printer className="h-4 w-4" /> Print / PDF
          </button>
        </div>
      </div>

      {/* Printable area */}
      <div id="price-list-print">
        {/* Print-only header (A4) */}
        <div className="print-only mb-4 hidden">
          <div className="print-header flex items-center justify-center gap-3">
            <img src="/slm-logo.png" alt="SLM" className="print-logo" />
            <div className="text-center">
              <p className="text-lg font-bold text-blue-800">Sri Lakshmi Agencies</p>
              <p className="text-sm text-slate-600">Ramakuppam</p>
              <p className="mt-1 text-xs text-slate-500">Price List · {today}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-slate-400">
            <ListOrdered className="h-10 w-10" />
            <p className="text-sm">No products match your search.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.brand} data-testid={`pricelist-brand-group-${g.brand}`} className="overflow-hidden rounded-xl border border-blue-100 bg-white">
                <div className="border-b border-blue-100 bg-blue-50 px-4 py-2.5">
                  <h3 className="text-sm font-bold text-blue-800">{g.brand}</h3>
                </div>
                <div className="overflow-hidden">
                  <table className="w-full table-auto text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:text-xs">
                        <th className="px-2 py-2 sm:px-4">Product</th>
                        <th className="whitespace-nowrap px-1.5 py-2 text-right sm:px-3">MRP</th>
                        <th className="whitespace-nowrap px-1.5 py-2 text-right sm:px-3">Piece</th>
                        <th className="whitespace-nowrap px-1.5 py-2 text-right sm:px-3">Case</th>
                        <th className="whitespace-nowrap px-2 py-2 text-right sm:px-4">Pcs/Case</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((p) => {
                        const mrp = displayMrp(p);
                        const piece = rateFor(p, "Piece");
                        const cased = rateFor(p, "Case");
                        return (
                          <tr key={p.id} data-testid={`pricelist-row-${p.id}`} className="border-t border-slate-50">
                            <td className="break-words px-2 py-2.5 font-medium text-slate-800 sm:px-4">{p.name}</td>
                            <td className="whitespace-nowrap px-1.5 py-2.5 text-right text-slate-500 sm:px-3">
                              {mrp != null ? <span className="line-through">{formatMoney(mrp)}</span> : dash}
                            </td>
                            <td className="whitespace-nowrap px-1.5 py-2.5 text-right font-semibold text-blue-700 sm:px-3">
                              {piece != null ? formatMoney(piece) : dash}
                            </td>
                            <td className="whitespace-nowrap px-1.5 py-2.5 text-right font-semibold text-blue-700 sm:px-3">
                              {cased != null ? formatMoney(cased) : dash}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2.5 text-right text-slate-600 sm:px-4">
                              {valid(p.case_quantity) ? Number(p.case_quantity) : dash}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
